import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import { icache } from '@dojo/framework/core/middleware/icache';
import { status } from '../middleware/status';
import {
	lccstate,
	userstate,
	createNlsMiddleware,
	recommended,
	contentTrack,
	Tracked,
	TrackedContent
} from '@holmescorp/lcc-state';
import { ParentTree } from '@holmescorp/lcc-interfaces';
import { TopicStatusIcon } from './topic-status-icon';
import { PTExamDate } from './pt-examdate';
import { TestStatusIcon } from './pt-test-status';

import css from '../css/progress-tracker.m.css';
import { isEmpty } from 'lodash-es';

const nls = createNlsMiddleware('ditabook');
const nlsSettings = createNlsMiddleware('settings');
const factory = create({
	icache,
	theme,
	nls,
	nlsSettings,
	userstate,
	lccstate,
	status,
	recommended,
	contentTrack
});

export const ProgressTracker = factory(
	({ middleware: { theme, nls, nlsSettings, userstate, lccstate, recommended, contentTrack } }) => {
		const themeCss = theme.classes(css);

		const { get, path } = userstate;
		const examdate = get(path('examdate'));
		const qp = get(path('quizPattern'));
		const contentorder = get(path('toolstate', 'contentorder'));
		const useRecommendedOrder = contentorder.extra === 'recommended';

		const hasAssessment = recommended.hasAssessment();

		const pathContent = useRecommendedOrder ? recommended : contentTrack;
		pathContent.recalculate();

		const tracked = pathContent.tracked;

		const {
			messages: {
				progress_tracker = '[ditabook] progress_tracker',
				dashboard_assess_text = '[ditabook] dashboard_assess_text',
				study_path_text = '[ditabook] study_path_text',
				dashboard_practice_type: practice_type = 'practice',
				dashboard_posttest_type = 'posttest',
				practice_exam_text = '[ditabook] practice_exam_text',
				dashboard_assess_tip_complete = '[ditabook] dashboard_assess_tip_complete',
				dashboard_assess_tip_incomplete = '[ditabook] dashboard_assess_tip_incomplete',
				practice_exam_tip_passed = '[ditabook] practice_exam_tip_passed',
				practice_exam_tip_incomplete = '[ditabook] practice_exam_tip_incomplete',
				post_test_text = '[ditabook] post_test_text',
				post_test_tip_passed = '[ditabook] post_test_tip_passed',
				post_test_tip_failed = '[ditabook] post_test_tip_failed',
				post_test_tip_incomplete = '[ditabook] post_test_tip_incomplete'
			}
		} = nls();

		const { dashboard_show_post_test = 'false', dashboard_show_practice_exam = 'true' } = nlsSettings().messages;

		const headerTitle = v('div', { classes: [themeCss.ptHeader] }, [progress_tracker]);

		const convertToBoolean = (s: string): boolean => {
			try {
				return JSON.parse(s);
			} catch (e) {
				return false;
			}
		};

		const spt = convertToBoolean(dashboard_show_post_test);
		const hasPE = convertToBoolean(dashboard_show_practice_exam);

		// Final Exam Date
		const examDate = w(PTExamDate, { examdate });
		// End Final Exam Date

		const get_tree = (practice_type: string): ParentTree => {
			const quizstate = get(path(practice_type));
			switch (practice_type) {
				case 'practice':
					return quizstate;

				case 'posttest':
					return quizstate.tree;
			}
			return {};
		};

		const has_scores = (tree: ParentTree) => {
			const roots: string[] = lccstate.get(lccstate.path('ditaroots'));
			return roots.some((k) => tree[k].data.percent > 0);
		};

		// Practice exam
		const addPracticeExam = () => {
			const practice_tree = get_tree(practice_type);
			const practiceExamTaken = has_scores(practice_tree);
			const practiceExamTip = practiceExamTaken ? practice_exam_tip_passed : practice_exam_tip_incomplete;

			return v('div', { classes: themeCss.ptExam }, [
				w(TestStatusIcon, {
					tip: practiceExamTip,
					isTaken: practiceExamTaken,
					isPassed: practiceExamTaken
				}),
				v('div', {}, [practice_exam_text])
			]);
		};
		// -- End Practice Exam

		//Post Test
		const addPostTest = () => {
			const post_tree = get_tree(dashboard_posttest_type);
			const score = get(path('posttest', 'score'));
			const postTestTaken = has_scores(post_tree);
			const postTestPassed = score >= 80;

			const postExamTip =
				postTestTaken && postTestPassed
					? post_test_tip_passed
					: postTestTaken
					? post_test_tip_failed
					: post_test_tip_incomplete;
			return v('div', { classes: themeCss.ptPostTest }, [
				w(TestStatusIcon, {
					tip: postExamTip,
					isTaken: postTestTaken,
					isPassed: postTestPassed
				}),
				v('div', {}, [post_test_text])
			]);
		};

		// Assess

		const assessTip = hasAssessment ? dashboard_assess_tip_complete : dashboard_assess_tip_incomplete;
		const assessIcon = v('div', { classes: themeCss.ptAssess }, [
			w(TestStatusIcon, {
				tip: assessTip,
				isTaken: hasAssessment,
				isPassed: hasAssessment
			}),
			v('div', {}, [dashboard_assess_text])
		]);
		// -- End Assess

		const displayOrder = () => {
			const content = useRecommendedOrder
				? (tracked as Tracked).recommended
				: Object.values((tracked as TrackedContent).content).flat();

			return content
				.filter((topics) => topics.completed)
				.concat(content.filter((topics) => !topics.completed))
				.map((topics) => {
					return w(TopicStatusIcon, { path: qp, title: topics.title, state: topics.completed });
				});
		};

		// Study path

		const studypath = v('div', { classes: themeCss.ptStudyPath }, [
			v('ol', { classes: themeCss.studypath }, displayOrder()),
			v('div', {}, [
				v('div', { classes: themeCss.studyPathLabelLine }, []),
				v('div', { classes: themeCss.studyPathLabelBar }, [
					v('span', { classes: themeCss.studyPathLabel }, [study_path_text])
				])
			])
		]);
		// -- End Study Path
		const ptClasses = [theme.variant(), themeCss.root];
		const ptChildren = [headerTitle, examDate, assessIcon, studypath, hasPE ? addPracticeExam() : null];
		if (spt) {
			if (hasPE) {
				ptClasses.push(themeCss.includePostAndPracticeTest);
			} else {
				ptClasses.push(themeCss.includePostTest);
			}
			ptChildren.push(addPostTest());
		}

		return v(
			'div',
			{
				xqa: 'dashboard-progress-tracker',
				role: 'table',
				classes: ptClasses
			},
			ptChildren
		);
	}
);

export default ProgressTracker;
